import { createUniqueId } from 'solid-js';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import '../Select/Styles.css';
import './Styles.css';

export const PhoneInput = (props: {
  label: string;
  value: string | null | undefined;
  placeholder?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  onInput?: (e: { target: { value: string } }) => void;
  required?: boolean;
  error?: string | string[] | null | boolean;
  dataTestId?: string;
  name?: string;
  class?: string;
  size?: 'sm' | 'md' | 'lg';
  maxDigits?: number;
}) => {
  const fallbackId = createUniqueId();
  const inputId = () => props.dataTestId ?? props.name ?? fallbackId;
  const errorId = () => `${inputId()}-error`;

  const hasError = () => {
    return (
      props.error !== false &&
      props.error !== null &&
      props.error !== undefined &&
      (Array.isArray(props.error) ? props.error.length > 0 : props.error !== '')
    );
  };

  const getErrorMessage = () => {
    if (!hasError()) return '';
    return Array.isArray(props.error) ? props.error[0] : props.error;
  };

  const heightClass = () => {
    if (props.size === 'sm') return 'h-[32px] text-xs';
    if (props.size === 'md') return 'h-[40px]';
    if (props.size === 'lg') return 'h-[48px]';
    return 'h-[40px]';
  };

  const formattedValue = () => {
    if (props.value !== undefined && props.value !== null) {
      return formatPhoneNumber(props.value);
    }
    return '';
  };

  const handleInputChange = (event: Event) => {
    props.onInput?.({
      target: { value: (event.target as HTMLInputElement).value },
    });

    const target = event.target as HTMLInputElement;
    const cursorPosition = target.selectionStart ?? 0;
    const oldValue = target.value;

    const newUnformattedValue = oldValue.replace(/[^0-9]/g, '');

    if (oldValue.replace(/[\s()-]/g, '') !== newUnformattedValue) {
      event.preventDefault();
      target.value = formattedValue();
      return;
    }

    if (newUnformattedValue.length > (props.maxDigits ?? 10)) {
      event.preventDefault();
      target.value = formattedValue();
      return;
    }

    const digitsBeforeCursor = oldValue
      .substring(0, cursorPosition)
      .replace(/[^0-9]/g, '').length;

    const newValue = formatPhoneNumber(newUnformattedValue);

    props.onChange?.(newUnformattedValue);

    let newCursorPosition = 0;
    let digitCount = 0;

    for (let i = 0; i < newValue.length; i++) {
      if (/[0-9]/.test(newValue[i])) {
        digitCount++;
      }
      if (digitCount > digitsBeforeCursor) break;
      newCursorPosition = i + 1;
    }

    setTimeout(() => {
      target.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  return (
    <div class={`relative w-full min-w-0 ${props.class ?? ''}`}>
      <input
        id={inputId()}
        type="tel"
        inputmode="tel"
        autocomplete="tel"
        class={`phone-input peer w-full px-3 py-2 bg-white border rounded-sm shadow-xs focus:outline-hidden
          disabled:bg-gray-100 disabled:border-gray-300 disabled:text-gray-500
          placeholder:opacity-0 focus:placeholder:opacity-100
          ${heightClass()}
          ${
            hasError() && !props.disabled
              ? 'border-error-red'
              : 'border-input-border hover:border-armstrong-primary focus:border-armstrong-primary'
          }`}
        value={formattedValue()}
        onInput={handleInputChange}
        onBlur={props.onBlur}
        placeholder={props.placeholder ?? ''}
        disabled={props.disabled}
        required={props.required ?? false}
        name={props.name}
        aria-invalid={hasError() || undefined}
        aria-describedby={hasError() ? errorId() : undefined}
        data-testid={props.dataTestId}
      />
      <label
        for={inputId()}
        class={`select-input-label absolute left-2 px-1 text-sm font-medium transition-all duration-200 ease-out pointer-events-none -top-2.5
          peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-gray-500 peer-focus:-top-2.5 peer-focus:text-sm peer-disabled:text-gray-400
          ${hasError() ? 'text-error-red! peer-focus:text-error-red! peer-placeholder-shown:text-error-red!' : 'text-armstrong-primary peer-focus:text-armstrong-primary'}`}
      >
        {props.label}
        {props.required && <span class="text-error-red"> *</span>}
      </label>
      {hasError() && (
        <div id={errorId()} class="text-error-red text-xs mt-1 px-2">
          {getErrorMessage()}
        </div>
      )}
    </div>
  );
};
